/* ============================================================
   FILE: assets/feature-tour.js
   PURPOSE: Coordinates the Feature Tour section — one <feature-tour>
   custom element wraps the accordion column, stage column, and
   mobile carousel column, so a click anywhere (accordion summary,
   hotspot dot, mobile button) can drive the same shared state:
   which tab is active, or none (= overview).

   PATTERN REUSED FROM collapsible-tabs.js
   ─────────────────────────────────────────
   Same shape: query relevant elements in connectedCallback, attach
   a native 'toggle' listener per <details> (toggle does not bubble,
   so it must be attached directly to each element, not delegated).
   Click handling (hotspots, mobile buttons) DOES bubble, so those
   use a single delegated listener on the custom element itself.

   MOBILE SLIDER — CONFIRMED PATTERN
   ─────────────────────────────────────────
   Matches products-bundle.js's ProductsBundleSlider and
   media-gallery.js's MediaGallery: Swiper is never instantiated
   directly — it's wrapped via `new window.FoxTheme.Carousel(el, options)`
   followed by `.init()`. The resulting instance exposes the real
   Swiper object at `.slider` (e.g. `.slider.slideTo()`), which is
   how both reference files drive slide position programmatically.
   ============================================================ */

if (!customElements.get('feature-tour')) {
  customElements.define(
    'feature-tour',
    class FeatureTour extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = null;
      }

      connectedCallback() {
        this.stage = this.querySelector('.feature-tour__stage');
        this.detailsElements = this.querySelectorAll('.feature-tour__details');
        this.mobileSlides = this.querySelectorAll('.feature-tour__mobile-slide');
        this.mobilePanels = this.querySelectorAll('.feature-tour__mobile-panel');
        this.mobileSliderEl = this.querySelector('.feature-tour__mobile-slider');
        this.mobileInnerEl = this.querySelector('.feature-tour__mobile-inner');

        // 'toggle' does not bubble — must attach per-element, same
        // as collapsible-tabs.js does.
        this.detailsElements.forEach((details) => {
          details.addEventListener('toggle', this.onToggle.bind(this));
        });

        // Click delegation covers both hotspots (in the stage
        // column) and mobile buttons (in the mobile column) with
        // one listener, since both bubble normally.
        this.addEventListener('click', this.onClick.bind(this));

        this.initMobileSlider();
      }

      onClick(event) {
        const hotspot = event.target.closest('.feature-tour-hotspot');
        if (hotspot) {
          this.onHotspotClick(hotspot);
          return;
        }

        const closeBtn = event.target.closest('.feature-tour__close');
        if (closeBtn) {
          this.onCloseClick();
          return;
        }

        const prevBtn = event.target.closest('.feature-tour__mobile-nav--prev');
        if (prevBtn) {
          this.onNavClick('prev');
          return;
        }

        const nextBtn = event.target.closest('.feature-tour__mobile-nav--next');
        if (nextBtn) {
          this.onNavClick('next');
          return;
        }

        const mobileBtn = event.target.closest('.feature-tour__mobile-btn');
        if (mobileBtn) {
          this.onMobileBtnClick(mobileBtn);
        }
      }

      /**
       * Native 'toggle' listener on a single <details>. This is the
       * single source of truth for "a tab opened/closed" — hotspot
       * clicks and mobile-button clicks both work by setting
       * details.open, then letting this handler react, rather than
       * duplicating the activation logic in three places.
       */
      onToggle(event) {
        const details = event.target;
        const index = details.dataset.tabIndex;

        if (details.open) {
          this.activateTab(index);
        } else {
          const anyStillOpen = Array.from(this.detailsElements).some((d) => d.open);
          if (!anyStillOpen) {
            this.activateOverview();
          }
        }
      }

      onHotspotClick(hotspot) {
        const index = hotspot.dataset.tabIndex;
        const details = this.querySelector(`.feature-tour__details[data-tab-index="${index}"]`);
        if (!details) return;
        // Toggling .open fires the native 'toggle' event, which
        // onToggle() picks up — this keeps a single code path for
        // "a tab became active/inactive" regardless of trigger.
        details.open = !details.open;
      }

      onCloseClick() {
        // Closing the open <details> fires the native 'toggle' event,
        // which onToggle() already handles by calling activateOverview()
        // once nothing else is open — same single code path as every
        // other trigger, rather than duplicating the revert logic here.
        this.detailsElements.forEach((details) => {
          if (details.open) details.open = false;
        });
      }

      onNavClick(direction) {
        const total = this.mobileSlides.length;
        if (total === 0) return;

        // Arrows are now visible by default, so activeIndex may be
        // null (nothing selected yet). Treat that as "just before the
        // first slide" for next, or "just after the last slide" for
        // prev, so the very first click still lands somewhere sensible
        // rather than doing nothing.
        const current = this.activeIndex !== null ? Number(this.activeIndex) : direction === 'next' ? -1 : total;

        let target = current + (direction === 'next' ? 1 : -1);
        target = Math.max(0, Math.min(total - 1, target)); // clamp — no wraparound, matches loop: false

        if (this.activeIndex !== null && target === Number(this.activeIndex)) return; // already at the first/last tab

        const details = this.querySelector(`.feature-tour__details[data-tab-index="${target}"]`);
        if (details) {
          // Opening this details fires the native 'toggle' event, which
          // onToggle() picks up — same single code path used by every
          // other trigger (hotspot, mobile button, etc.), including the
          // slideTo() re-centering already handled inside activateTab().
          details.open = true;
        } else {
          this.activateTab(String(target));
        }
      }

      onMobileBtnClick(btn) {
        const slide = btn.closest('.feature-tour__mobile-slide');
        const index = btn.dataset.tabIndex;
        const wasActive = slide && slide.classList.contains('is-active');

        // Keep the desktop accordion's <details> in sync even when
        // it's visually hidden on mobile, so state stays consistent
        // if the viewport is resized without a reload.
        const details = this.querySelector(`.feature-tour__details[data-tab-index="${index}"]`);
        if (details) {
          details.open = !wasActive;
        } else {
          // No matching <details> found (shouldn't normally happen,
          // but fail safe rather than fail silent).
          if (wasActive) {
            this.activateOverview();
          } else {
            this.activateTab(index);
          }
        }
      }

      activateTab(index) {
        this.activeIndex = index;

        this.querySelectorAll('.feature-tour__image').forEach((img) => {
          img.classList.toggle('is-active', img.dataset.tabIndex === index);
        });

        if (this.stage) this.stage.classList.add('has-active-tab');

        this.mobileSlides.forEach((slide) => {
          slide.classList.toggle('is-active', slide.dataset.tabIndex === index);
        });

        this.mobilePanels.forEach((panel) => {
          panel.hidden = panel.dataset.tabIndex !== index;
        });

        if (this.mobileInnerEl) this.mobileInnerEl.classList.add('has-active-slide');

        if (this.sliderInstance && this.sliderInstance.slider && typeof this.sliderInstance.slider.slideTo === 'function') {
          this.sliderInstance.slider.slideTo(Number(index));
        }
      }

      activateOverview() {
        this.activeIndex = null;

        this.querySelectorAll('.feature-tour__image').forEach((img) => {
          img.classList.toggle('is-active', img.dataset.tabIndex === 'overview');
        });

        if (this.stage) this.stage.classList.remove('has-active-tab');

        if (this.mobileInnerEl) this.mobileInnerEl.classList.remove('has-active-slide');

        this.mobileSlides.forEach((slide) => slide.classList.remove('is-active'));
        this.mobilePanels.forEach((panel) => {
          panel.hidden = true;
        });
      }

      initMobileSlider() {
        if (!this.mobileSliderEl) return;

        // FoxTheme.Carousel is the confirmed wrapper this theme uses
        // for every other slider (products-bundle-slider, media-gallery).
        // If it's ever unavailable for some reason, fail safe rather
        // than throw — mobile buttons still work for selecting a tab,
        // just without swipe/drag or the progress-bar fill.
        if (!window.FoxTheme || typeof window.FoxTheme.Carousel !== 'function') return;

        const sliderOptions = {
          slidesPerView: 'auto',
          spaceBetween: 24, // [TUNE] gap between mobile tab buttons — matches CSS mobile rhythm
          freeMode: true,
          centeredSlides: true, // keeps the active/selected button centered in the row, per request
          loop: false,
          threshold: 2,
          navigation: {
            nextEl: this.querySelector('.feature-tour__mobile-nav--next'),
            prevEl: this.querySelector('.feature-tour__mobile-nav--prev'),
          },
        };

        this.sliderInstance = new window.FoxTheme.Carousel(this.mobileSliderEl, sliderOptions);
        this.sliderInstance.init();
      }
    }
  );
}
